import { useEffect, useState } from 'react';
import { Plus, Loader2, Ticket, Trash2 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

type DiscountType = 'percent' | 'amount' | 'free_month';
type Duration = 'once' | 'forever' | 'repeating';

interface PromoCode {
  id: string;
  code: string;
  discount_type: DiscountType;
  value: string;
  duration: Duration;
  duration_in_months: number | null;
  max_redemptions: number | null;
  expires_at: string | null;
  times_redeemed: number;
  is_active: boolean;
  created_at: string;
}

interface FormData {
  code: string;
  discount_type: DiscountType;
  value: string;
  duration: Duration;
  duration_in_months: string;
  max_redemptions: string;
  expires_at: string;
}

const INITIAL_FORM: FormData = {
  code: '',
  discount_type: 'percent',
  value: '',
  duration: 'once',
  duration_in_months: '',
  max_redemptions: '',
  expires_at: '',
};

function describeDiscount(p: PromoCode): string {
  if (p.discount_type === 'free_month') {
    const m = p.duration_in_months ?? 1;
    return m === 1 ? '1 free month' : `${m} free months`;
  }
  const v = Number(p.value);
  const base = p.discount_type === 'percent' ? `${v}% off` : `$${v.toFixed(2)} off`;
  if (p.duration === 'repeating') return `${base} for ${p.duration_in_months ?? 1} mo`;
  if (p.duration === 'forever') return `${base} forever`;
  return `${base} once`;
}

const SubscriptionPromos = () => {
  const addToast = useUIStore((s) => s.addToast);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  async function fetchCodes() {
    try {
      setIsLoading(true);
      const res = await api.get<{ codes: PromoCode[] }>('/admin/subscription-promos');
      setCodes(res.codes);
    } catch (err: any) {
      addToast(err?.message || 'Failed to load promo codes', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreate() {
    setForm(INITIAL_FORM);
    setModalOpen(true);
  }

  async function handleSave() {
    if (form.discount_type !== 'free_month' && !form.value) {
      addToast('A discount value is required', 'error');
      return;
    }
    try {
      setIsSaving(true);
      const payload: Record<string, unknown> = {
        discount_type: form.discount_type,
        duration: form.discount_type === 'free_month' ? 'repeating' : form.duration,
      };
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.discount_type !== 'free_month') payload.value = parseFloat(form.value);
      if (
        form.discount_type === 'free_month' ||
        form.duration === 'repeating'
      ) {
        payload.duration_in_months = parseInt(form.duration_in_months) || 1;
      }
      if (form.max_redemptions) payload.max_redemptions = parseInt(form.max_redemptions);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();

      await api.post('/admin/subscription-promos', payload);
      addToast('Promo code created', 'success');
      setModalOpen(false);
      fetchCodes();
    } catch (err: any) {
      addToast(err?.message || 'Failed to create promo code', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function deactivate(id: string) {
    try {
      await api.delete(`/admin/subscription-promos/${id}`);
      addToast('Promo code deactivated', 'success');
      setCodes((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: false } : c)));
    } catch (err: any) {
      addToast(err?.message || 'Failed to deactivate', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  const showMonths = form.discount_type === 'free_month' || form.duration === 'repeating';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Promos</h1>
          <p className="text-gray-600 mt-1">
            Stripe discount codes customers can apply at subscription checkout.
          </p>
        </div>
        <Button variant="primary" size="md" onClick={openCreate}>
          <Plus className="w-5 h-5 mr-2" />
          New Code
        </Button>
      </div>

      {codes.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <Ticket className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No promo codes yet</h3>
            <p className="text-gray-600 mb-6">
              Create a code to offer a discount on subscriptions.
            </p>
            <Button variant="primary" size="md" onClick={openCreate}>
              <Plus className="w-5 h-5 mr-2" />
              New Code
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Discount</th>
                <th className="px-4 py-3 font-medium">Redemptions</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {codes.map((c) => (
                <tr key={c.id} className={c.is_active ? '' : 'opacity-50'}>
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">{c.code}</td>
                  <td className="px-4 py-3 text-gray-700">{describeDiscount(c)}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {c.times_redeemed}
                    {c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.is_active ? (
                      <Badge variant="green">Active</Badge>
                    ) : (
                      <Badge variant="red">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.is_active && (
                      <button
                        onClick={() => deactivate(c.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="New Promo Code">
        <div className="space-y-4">
          <Input
            label="Code (optional)"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="Auto-generated if blank"
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Discount Type"
              value={form.discount_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, discount_type: e.target.value as DiscountType }))
              }
              options={[
                { label: 'Percent off', value: 'percent' },
                { label: 'Amount off ($)', value: 'amount' },
                { label: 'Free month(s)', value: 'free_month' },
              ]}
            />
            {form.discount_type !== 'free_month' && (
              <Input
                label={form.discount_type === 'percent' ? 'Percent (1-100)' : 'Amount ($)'}
                type="number"
                value={form.value}
                onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                placeholder="0"
              />
            )}
          </div>

          {form.discount_type !== 'free_month' && (
            <Select
              label="Applies"
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value as Duration }))}
              options={[
                { label: 'Once', value: 'once' },
                { label: 'Every billing cycle (forever)', value: 'forever' },
                { label: 'For N months', value: 'repeating' },
              ]}
            />
          )}

          {showMonths && (
            <Input
              label="Number of months"
              type="number"
              value={form.duration_in_months}
              onChange={(e) => setForm((f) => ({ ...f, duration_in_months: e.target.value }))}
              placeholder="1"
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Max redemptions (optional)"
              type="number"
              value={form.max_redemptions}
              onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited"
            />
            <Input
              label="Expires (optional)"
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isSaving ? 'Creating...' : 'Create Code'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPromos;
